import type {ReactElement} from 'react';
import {useRef, useEffect, useState} from 'react';
import React from 'react';
import {useDownloadPanelData} from '@Components/download-panel/download-panel.context';
import {ControlledList} from '@Components/controlled-list';
import {getPosterWidthAndHeight, getUnitsFromPosterTypeObject, getWidthAndHeightInPixels, isCustomSize, PosterTypeUnits} from '@Libraries/poster-object.library';
import {VideoDesignSizeOptionType} from '@Libraries/poster-download-library';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {BaseSizeDropdownSelector} from '@Components/download-panel/components/base-size-dropdown-selector/base-size-dropdown-selector';
import {BaseSizeDropdown} from '@Components/download-panel/components/base-size-dropdown/base-size-dropdown';
import {useAppDispatch} from '@/hooks';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {MODALS_IDS} from '@Components/modal-container';

export function DownloadVideoDesignSizeOption(): ReactElement {
  const {setScale, defaultOptions, posterObject, videoScale, setVideoScale} = useDownloadPanelData();
  const initialRender = useRef(true);
  const [doShowOriginalValueForVideo, setDoShowOriginalValueForVideo] = useState(false);
  const [videoOriginalMatchingValue, setVideoOriginalMatchingValue] = useState<VideoDesignSizeOptionType | null>();
  const dispatch = useAppDispatch();

  useEffect(() => {
    const areUnitsInPixel = getUnitsFromPosterTypeObject(posterObject) === PosterTypeUnits.PX;
    if (areUnitsInPixel) {
      const maxValue = Math.max(
        isCustomSize(posterObject.type.name) ? posterObject.userWidth : posterObject.type.width,
        isCustomSize(posterObject.type.name) ? posterObject.userHeight : posterObject.type.height
      );
      if (maxValue === 640 || maxValue === 1280 || maxValue === 1920 || maxValue === 3840) {
        switch (maxValue) {
          case 640:
            setVideoOriginalMatchingValue(VideoDesignSizeOptionType.SD);
            break;
          case 1280:
            setVideoOriginalMatchingValue(VideoDesignSizeOptionType.HD);
            break;
          case 1920:
            setVideoOriginalMatchingValue(VideoDesignSizeOptionType.FULL_HD);
            break;
          case 3840:
            setVideoOriginalMatchingValue(VideoDesignSizeOptionType.UHD_4K);
            break;
        }
      } else {
        if (maxValue > 3840) {
          setVideoScale(VideoDesignSizeOptionType.UHD_4K);
          setDoShowOriginalValueForVideo(false);
          setVideoOriginalMatchingValue(null);
          setInitialScale(3840 / maxValue);
          return;
        }
        setVideoScale(VideoDesignSizeOptionType.ORIGINAL);
        setDoShowOriginalValueForVideo(true);
      }
      setInitialScale(1);
    } else {
      setDoShowOriginalValueForVideo(false);
      setVideoOriginalMatchingValue(null);
      setVideoScale(VideoDesignSizeOptionType.FULL_HD);
      setScaleForVideo(1920);
    }
  }, [posterObject.type]);

  const getTextForVideoScale = (): string => {
    switch (videoScale) {
      case VideoDesignSizeOptionType.SD:
        return videoOriginalMatchingValue === VideoDesignSizeOptionType.SD ? `480p (${window.i18next.t('pmwjs_original')})` : '480p';
      case VideoDesignSizeOptionType.HD:
        return videoOriginalMatchingValue === VideoDesignSizeOptionType.HD ? `720p (${window.i18next.t('pmwjs_original')})` : '720p';
      case VideoDesignSizeOptionType.FULL_HD:
        return videoOriginalMatchingValue === VideoDesignSizeOptionType.FULL_HD ? `1080p · HD (${window.i18next.t('pmwjs_original')}) ` : '1080p · HD';
      case VideoDesignSizeOptionType.UHD_4K:
        return videoOriginalMatchingValue === VideoDesignSizeOptionType.UHD_4K ? `2160p · 4K (${window.i18next.t('pmwjs_original')})` : '2160p · 4K';
      case VideoDesignSizeOptionType.ORIGINAL:
        return `${isCustomSize(posterObject.type.name) ? posterObject.userWidth : posterObject.type.width} x ${isCustomSize(posterObject.type.name) ? posterObject.userHeight : posterObject.type.height}px · ${window.i18next.t('pmwjs_original')}`;
      default:
        return '720p';
    }
  };
  const getSelectorTextForDropdown = (): string => {
    return getTextForVideoScale();
  };

  const getSelectorForDropdown = (): ReactElement => {
    return (
      <div>
        <BaseSizeDropdownSelector text={getSelectorTextForDropdown()} />
      </div>
    );
  };

  const setInitialScale = (value: number): void => {
    if (defaultOptions?.scale && initialRender.current) {
      return;
    }

    initialRender.current = false;
    setScale(value);
  };

  const setScaleForVideo = (scaledDimension: number): void => {
    const {width: convertedWidth, height: convertedHeight} = getWidthAndHeightInPixels(posterObject);
    if (convertedWidth < convertedHeight) {
      setInitialScale(scaledDimension / convertedHeight);
    } else {
      setInitialScale(scaledDimension / convertedWidth);
    }
  };

  const onClickListItem = (id: VideoDesignSizeOptionType): void => {
    setVideoScale(id);
    switch (id) {
      case VideoDesignSizeOptionType.UHD_4K:
        setScaleForVideo(3840);
        break;
      case VideoDesignSizeOptionType.FULL_HD:
        setScaleForVideo(1920);
        break;
      case VideoDesignSizeOptionType.HD:
        setScaleForVideo(1280);
        break;
      case VideoDesignSizeOptionType.SD:
        setScaleForVideo(640);
        break;
      case VideoDesignSizeOptionType.ORIGINAL:
        setScale(1);
        break;
      default:
        break;
    }
    dispatch(closeModal(MODALS_IDS.MORE_OPTIONS_MODAL));
  };

  const getTextForEachOption = (id: VideoDesignSizeOptionType): string => {
    let text: string;
    switch (id) {
      case VideoDesignSizeOptionType.SD:
        text = '480p';
        break;
      case VideoDesignSizeOptionType.HD:
        text = '720p';
        break;
      case VideoDesignSizeOptionType.FULL_HD:
        text = '1080p · HD';
        break;
      case VideoDesignSizeOptionType.UHD_4K:
        text = `2160p · 4K`;
        break;
      case VideoDesignSizeOptionType.ORIGINAL:
        return `${getPosterWidthAndHeight(posterObject).width} x ${getPosterWidthAndHeight(posterObject).height}${getUnitsFromPosterTypeObject(posterObject)} (${window.i18next.t('pmwjs_original')})`;
      default:
        return '';
    }

    if (videoOriginalMatchingValue === id) {
      return `${window.i18next.t('pmwjs_original')} · ${text}`;
    }
    return text;
  };

  const getItemsForPopup = (): ControlledListItemType[] => {
    const items: ControlledListItemType[] = [];
    if (doShowOriginalValueForVideo) {
      items.push({
        id: VideoDesignSizeOptionType.ORIGINAL,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
        text: getTextForEachOption(VideoDesignSizeOptionType.ORIGINAL),
        isSelected: videoScale === VideoDesignSizeOptionType.ORIGINAL,
      });
    }
    items.push(
      {
        id: VideoDesignSizeOptionType.SD,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
        text: getTextForEachOption(VideoDesignSizeOptionType.SD),
        isSelected: videoScale === VideoDesignSizeOptionType.SD,
      },
      {
        id: VideoDesignSizeOptionType.HD,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
        text: getTextForEachOption(VideoDesignSizeOptionType.HD),
        isSelected: videoScale === VideoDesignSizeOptionType.HD,
      },
      {
        id: VideoDesignSizeOptionType.FULL_HD,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
        text: getTextForEachOption(VideoDesignSizeOptionType.FULL_HD),
        isSelected: videoScale === VideoDesignSizeOptionType.FULL_HD,
      },
      {
        id: VideoDesignSizeOptionType.UHD_4K,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
        text: getTextForEachOption(VideoDesignSizeOptionType.UHD_4K),
        isSelected: videoScale === VideoDesignSizeOptionType.UHD_4K,
      }
    );

    return items;
  };

  const getPopupForDropdown = (): ReactElement => {
    return (
      <div className="flex flex-v-row">
        <ControlledList onClick={onClickListItem} items={getItemsForPopup()} className="spacing-m-0" />
      </div>
    );
  };

  return (
    <BaseSizeDropdown
      modalTitle={window.i18next.t('pmwjs_size')}
      popup={getPopupForDropdown()}
      selector={getSelectorForDropdown()}
      showSmallWidthPopup={!doShowOriginalValueForVideo && !videoOriginalMatchingValue}
      popupSpacingClass="spacing-p-r-3 spacing-p-l-3 spacing-p-t-3 spacing-p-b-1"
    />
  );
}
